// The LIB circulation-desk route subtree (console slice 1). Code-based routes
// per DEV-GUIDE-002 §4 — a package-composed subtree hand-writes `.lazy()` +
// `createLazyRoute`. This factory hangs the `/library/desk` tree off the
// portal's root route so router.tsx stays the single wiring point.
//
// GATING is per-screen, not global (router.tsx's documented choice): the desk
// LAYOUT route (DeskLayout) re-checks lib:desk via useSession/<Can> before it
// renders any child — a defence-in-depth mirror of the API's rbac('lib:desk')
// guard on every /circulation route (the UI can only HIDE; the gateway is the
// authority, ADR-001 §14.2). Anonymous → /login; signed-in-without-lib:desk →
// an explicit denied panel, never a blank Outlet.
import { lazyRoute } from "@erp/ui/route-kit";
import { Outlet, createRoute } from "@tanstack/solid-router";
import { libraryQueries } from "../index.js";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { PageHeading } from "@erp/ui/page-heading";
import { HubGrid } from "@erp/ui/hub-grid";
import { MODULE_HUE } from "@erp/ui/category-hue";
import { ArrowLeftRight } from "lucide-solid";
import { panelSearch, type PanelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/**
 * Renewals URL-as-state (DEV-GUIDE-002 §4.1). The guide's ideal is a Zod-direct
 * validateSearch with `.catch()`; zod is not a direct dependency of any portal
 * here (and this slice may not run `pnpm install`), so this is a hand-written
 * validator with the SAME self-healing contract: a garbage `?status=` heals to
 * the default rather than throwing. Swap for `LoanStatus.catch(...)` once zod
 * is a portal dep. status drives the LoanListQuery filter.
 *
 * NO `cursor` (2026-09-22). The register is an INFINITE stream now: the stage
 * and the search still live in the URL — a shared link restores exactly the
 * register someone was looking at — but the cursor does not, because it no
 * longer names a page. It names a position inside one accumulating list. A
 * stale `?cursor=` on an old bookmark simply falls out here, which is the
 * self-healing contract this validator already had.
 */
export const RENEWAL_STATUSES = ["Active", "Overdue", "Returned"] as const;
export type RenewalStatus = (typeof RENEWAL_STATUSES)[number];
/** Both params OPTIONAL so a bare `to="/library/desk/renewals"` link needs no
 * `search` — the screen defaults an absent status to "Active" (RENEWALS_DEFAULT_STATUS). */
export interface RenewalsSearch {
  status?: RenewalStatus;
  search?: string;
}
export const RENEWALS_DEFAULT_STATUS: RenewalStatus = "Active";

/**
 * The closed panel set for `/library/desk/issue` — the audited additional-options
 * surface. A `?panel=` outside this set heals to "no panel" (panelSearch), so a
 * shared link can never open a surface this route does not own.
 */
export const ISSUE_PANELS = ["options"] as const;

export function validateIssueSearch(input: Record<string, unknown>): PanelSearch {
  return panelSearch(ISSUE_PANELS, input);
}

export function validateRenewalsSearch(input: Record<string, unknown>): RenewalsSearch {
  const status = RENEWAL_STATUSES.includes(input.status as RenewalStatus) ? (input.status as RenewalStatus) : undefined;
  const search = typeof input.search === "string" && input.search.length > 0 ? input.search : undefined;
  return { status, search };
}

/** Fixed keyset page size for the desk loan register. */
export const RENEWALS_PAGE_SIZE = 25;

const DESK_LINKS = [
  { to: "/library/desk/issue", label: "Issue books", blurb: "Check copies out to a patron", id: "desk-nav-issue" },
  { to: "/library/desk/return", label: "Return books", blurb: "Check a copy in and settle fines", id: "desk-nav-return" },
  { to: "/library/desk/renewals", label: "Renewals", blurb: "Extend an active loan's due date", id: "desk-nav-renewals" },
  { to: "/library/desk/devices", label: "Connect a phone", blurb: "Pair a phone as a wireless scanner", id: "desk-nav-devices" },
] as const;

/**
 * The desk shell: gates on lib:desk, then renders the active child. The gating
 * sequence lives in @erp/ui/console-layout — the same component the admin
 * consoles and the student account area use. The tab strip is retired in favour
 * of the shell sidebar (modules/lib's libStaffNav) + header breadcrumbs.
 */
function DeskLayout() {
  return (
    <ConsoleLayout
      perm="lib:desk"
      deniedMessage="This account cannot use the circulation desk. Ask an administrator for desk access."
      skeletonId="desk-skeleton"
      deniedId="desk-denied"
      unreachableId="desk-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/** `/library/desk` index — the landing with links into the three desk flows. */
function DeskLanding() {
  return (
    <div>
      {/* The desk's identity capsule (DEV-GUIDE-002 §12.5): Issue, Return and
          Renewals wear circulation cyan with the same glyph, so the landing they
          hang off wears it too. */}
      <PageHeading
        icon={ArrowLeftRight}
        hue={MODULE_HUE.circulation}
        title="Circulation desk"
        subtitle="Issue, return and renew"
        help={{
          title: "Where books change hands",
          children: (
            <p>
              Issue checks copies out to a patron, Return checks them back in and settles any fine that fell
              due, and Renewals extends a loan that is still out. Connect a phone pairs a handset as a wireless
              scanner, so a barcode read on the phone lands in whichever field is waiting here.
            </p>
          ),
        }}
      />
      {/* The desk landing takes the same grid as every console landing —
          @erp/ui/hub-grid — rather than its own copy of Link > Card >
          CardHeader with a badge standing in for a button. */}
      <HubGrid id="desk-landing" items={DESK_LINKS} hue={MODULE_HUE.circulation} columns={3} />
    </div>
  );
}

/**
 * Build the `/library/desk` subtree under a portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). Screens
 * close their own `.lazy()` loop with `createLazyRoute('/library/desk/<x>')`.
 */
export function makeLibDeskRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const deskRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/desk",
    component: DeskLayout,
  });

  const deskIndexRoute = createRoute({
    getParentRoute: () => deskRoute,
    path: "/",
    component: DeskLanding,
  });

  const issueRoute = createRoute({
    getParentRoute: () => deskRoute,
    path: "issue",
    validateSearch: validateIssueSearch, // self-healing panel params (§4.1)
  }).lazy(lazyRoute(() => import("../screens/staff/IssueBook.js")));

  const returnRoute = createRoute({
    getParentRoute: () => deskRoute,
    path: "return",
  }).lazy(lazyRoute(() => import("../screens/staff/ReturnBook.js")));

  // The phone-pairing surface (SCAN-4). Rides the same lib:desk gate as the rest
  // of the desk — pairing a scanner IS a desk action.
  const devicesRoute = createRoute({
    getParentRoute: () => deskRoute,
    path: "devices",
  }).lazy(lazyRoute(() => import("../screens/staff/ConnectDevices.js")));

  const renewalsRoute = createRoute({
    getParentRoute: () => deskRoute,
    path: "renewals",
    validateSearch: validateRenewalsSearch, // self-healing (§4.1); see the note above
    loaderDeps: ({ search }) => ({ status: search.status, search: search.search }),
    loader: ({ context, deps }) => {
      // ONE definition serves loader + screen + tests (§4.3 Do). The filters
      // object here MUST equal the screen's so both hit the same cache entry —
      // an absent status heals to the default on BOTH sides. Warmed, not awaited
      // (audit UX-01): an awaited ensure held the route and the desk saw an empty
      // outlet for the whole API wait; the screen paints its bones from
      // `queryState(...).pending()` instead. `prefetchInfiniteQuery` warms the
      // FIRST page only (its `pages` default is 1) into the same accumulating
      // entry the screen reads, so the reader's scroll fetches page two.
      void context.queryClient.prefetchInfiniteQuery(
        libraryQueries.loansInfinite({
          limit: RENEWALS_PAGE_SIZE,
          status: deps.status ?? RENEWALS_DEFAULT_STATUS,
          search: deps.search,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/staff/Renewals.js")));

  return [deskRoute.addChildren([deskIndexRoute, issueRoute, returnRoute, devicesRoute, renewalsRoute])];
}
